import { ReservationStatus } from '@/types';
import RESERVATION_STATUS_OPTIONS from './reservationStatus';

/*
 Lógica de transiciones de estado para reservas.
 El objetivo es exponer funciones que permitan obtener las opciones
 que debe mostrar el `IconSelect` en `ReservationModal` dependiendo
 del estado actual de la reserva.

 Reglas derivadas del documento de flujo de estados provisto:
 - Incluimos siempre el estado actual como opción visible.
 - Solo mostramos las transiciones permitidas desde el estado actual
   (más el propio estado) para que el administrador pueda cambiar a
   estados válidos.
*/

type StatusValue = string;

export const ALLOWED_STATUS_TRANSITIONS: Record<string, StatusValue[]> = {
  // Estado inicial: pendiente de pago
  [ReservationStatus.PENDING_PAYMENT]: [
    ReservationStatus.CONFIRMED,
    ReservationStatus.PAYMENT_FAILED,
    ReservationStatus.CANCELED,
  ],

  // Pago fallido: puede reintentar o cancelar
  [ReservationStatus.PAYMENT_FAILED]: [ReservationStatus.CONFIRMED, ReservationStatus.CANCELED],

  // Confirmada: puede pasar a preparación o cancelarse
  [ReservationStatus.CONFIRMED]: [ReservationStatus.PREPARING_SHIPMENT, ReservationStatus.CANCELED],

  // En preparación -> En envío
  [ReservationStatus.PREPARING_SHIPMENT]: [ReservationStatus.IN_DELIVERY],

  // En envío -> Entregado / En uso  OR volver a preparación si hay problema
  [ReservationStatus.IN_DELIVERY]: [ReservationStatus.ACTIVE, ReservationStatus.PREPARING_SHIPMENT],

  // Activo (en uso) -> incidencia en uso o iniciar devolución
  [ReservationStatus.ACTIVE]: [ReservationStatus.IN_USE_ISSUE, ReservationStatus.PENDING_RETURN, ReservationStatus.IN_RETURN],

  // Incidencia en uso -> resolver a activo, forzar devolución o cancelar
  [ReservationStatus.IN_USE_ISSUE]: [ReservationStatus.ACTIVE, ReservationStatus.IN_RETURN, ReservationStatus.CANCELED],

  // En devolución (transporte de regreso) -> Devuelto o incidencia en la devolución
  [ReservationStatus.IN_RETURN]: [ReservationStatus.RETURNED, ReservationStatus.RETURN_ISSUE],

  // Pendiente de devolución -> puede iniciar el transporte de devolución
  [ReservationStatus.PENDING_RETURN]: [ReservationStatus.IN_RETURN],

  // Al recibir en almacén se puede marcar como finalizada o abrir incidencia
  [ReservationStatus.RETURNED]: [ReservationStatus.COMPLETED, ReservationStatus.RETURN_ISSUE],

  // Incidencia en devolución -> una vez resuelta, siempre Finalizada
  [ReservationStatus.RETURN_ISSUE]: [ReservationStatus.COMPLETED],

  // Finalizada y Cancelada son estados terminales (sin transiciones hacia otros)
  [ReservationStatus.COMPLETED]: [],
  [ReservationStatus.CANCELED]: [],
};

/**
 * Devuelve las opciones (objetos de `reservationStatus`) que debe mostrar
 * el selector para un `currentStatus` dado. Siempre incluye el estado
 * actual y las transiciones permitidas desde ese estado.
 */
export function getAllowedStatusOptions(currentStatus?: string) {
  // si no hay estado actual, devolvemos todas las opciones
  if (!currentStatus) return RESERVATION_STATUS_OPTIONS;

  const allowed = ALLOWED_STATUS_TRANSITIONS[currentStatus] ?? [];
  const set = new Set<string>([...allowed, currentStatus]);

  return RESERVATION_STATUS_OPTIONS.filter((o) => set.has(o.value));
}

/**
 * Valida si una transición de `from` -> `to` está permitida.
 * Se considera permitido permanecer en el mismo estado.
 */
export function isTransitionAllowed(from?: string, to?: string) {
  if (!from || !to) return false;
  if (from === to) return true;
  const allowed = ALLOWED_STATUS_TRANSITIONS[from] ?? [];
  return allowed.includes(to);
}

export default {
  ALLOWED_STATUS_TRANSITIONS,
  getAllowedStatusOptions,
  isTransitionAllowed,
};
